function csrfToken() {
  return document.querySelector('[name="csrf-token"]')?.content ?? ''
}

// POST a new ChatBot::Source. `fields` are chat_bot_source[*] params (null/false
// values are skipped, true becomes 'true'). Throws failMessage on a non-OK response
// and invalidMessage (or the server's error) if any requiredField is missing, unless
// `accept` approves the response as it is.
async function postSource(
  fields,
  {
    requiredFields = ['source_id'],
    accept = () => false,
    failMessage,
    invalidMessage,
  } = {}
) {
  const formData = new FormData()
  for (const [key, value] of Object.entries(fields)) {
    if (value === null || value === undefined || value === false) continue
    formData.append(`chat_bot_source[${key}]`, value === true ? 'true' : value)
  }

  const response = await fetch('/chat_bot/sources', {
    method: 'POST',
    body: formData,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': csrfToken(),
    },
  })

  if (!response.ok) throw new Error(failMessage)
  const json = await response.json()
  if (!accept(json) && requiredFields.some((field) => !json[field])) {
    throw new Error(json.error?.join(', ') || invalidMessage)
  }
  return json
}

export async function createSourceRecord(
  file,
  { conversationOnly = false } = {}
) {
  return postSource(
    {
      file_content_type: file.type,
      filename: file.name,
      source_type: 'file',
      conversation_only: conversationOnly || null,
    },
    {
      requiredFields: ['source_id', 's3_key'],
      failMessage: 'Failed to create source record',
      invalidMessage: 'File type not supported or validation failed',
    }
  )
}

// Resolves to { simplero_link: true } (and no source) when the link is an admin page in one of
// the user's own Simplero accounts — see ChatBot::SourcesController#create.
export async function createUrlSource(url, { conversationOnly = false } = {}) {
  return postSource(
    { source_type: 'url', url, conversation_only: conversationOnly || null },
    {
      accept: (json) => json.simplero_link,
      failMessage: 'Failed to create link source',
      invalidMessage: 'Could not create link source',
    }
  )
}

export async function createTextSource(
  text,
  { conversationOnly = false, title = null } = {}
) {
  return postSource(
    {
      source_type: 'plain_text',
      text,
      title,
      conversation_only: conversationOnly || null,
    },
    {
      failMessage: 'Failed to create text source',
      invalidMessage: 'Could not create text source',
    }
  )
}

export async function getSignedUploadUrl(sourceId) {
  const response = await fetch(
    `/chat_bot/sources/prepare_upload?source_id=${sourceId}`,
    {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': csrfToken(),
      },
    }
  )

  if (!response.ok) throw new Error('Failed to get upload URL')
  return response.json()
}

export async function uploadFileToS3(file, s3Key, uploadData, onProgress) {
  let uploaderModule
  try {
    uploaderModule = await import(
      '~/admin/components/asset_uploader/helpers.js'
    )
  } catch (error) {
    throw new Error(
      'File uploader could not load. Reload the page and try again.',
      {
        cause: error,
      }
    )
  }
  const { createUploader } = uploaderModule
  const uploader = createUploader(
    uploadData.signer_url,
    uploadData.fields.AWSAccessKeyId,
    uploadData.bucket,
    true
  )

  const evaporate = await uploader
  return new Promise((resolve, reject) => {
    evaporate.add({
      file,
      name: s3Key,
      progress: (percent, stats) => onProgress?.(percent, stats),
      complete: resolve,
      error: (message) => reject(new Error(message)),
    })
  })
}

export async function markSourceProcessing(sourceId) {
  const response = await fetch(`/chat_bot/sources/${sourceId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify({ chat_bot_source: { status: 'processing' } }),
  })

  if (!response.ok) throw new Error('Failed to mark source as processing')
  return response.json()
}
